import uuid from 'uuid';
import cacheManager from '../loading/resources/cacheManager';
import { reportError } from './ErrorReporting';

const streamValidationTimer = 5000;
let serviceWorkerInfo;
let readableStreamValidationId;
let serviceWorkerValidationId;

const validateReadableStream = () => {
    if (cacheManager.allCacheItemsAreDone()) {
        return;
    }

    const message = {
        type: 'readable-stream-health-request',
    };
    navigator.serviceWorker.controller.postMessage(message);

    serviceWorkerValidationId = setTimeout(() => {
        reportError(`No readable-stream-health-response message for ${streamValidationTimer}ms. Lost SW connection.`);
    }, streamValidationTimer);
};

const ServiceWorkerService = {
    isControlledByServiceWorker() {
        return 'serviceWorker' in navigator && navigator.serviceWorker.controller !== null;
    },

    browserSupportsServiceWorker() {
        return 'serviceWorker' in navigator;
    },

    addServiceWorkerHealthMonitor() {
        window.removeEventListener('online', validateReadableStream);
        window.addEventListener('online', () => {
            readableStreamValidationId = setTimeout(validateReadableStream, streamValidationTimer);
        });
    },

    clearReadableStreamValidation() {
        if (readableStreamValidationId) {
            clearTimeout(readableStreamValidationId);
            readableStreamValidationId = null;
        }
    },

    clearServiceWorkerValidation() {
        if (serviceWorkerValidationId) {
            clearTimeout(serviceWorkerValidationId);
            serviceWorkerValidationId = null;
        }
    },

    notifyWhenServiceWorkerIsActive() {
        if (this.isControlledByServiceWorker()) {
            console.log('Is already controlled by sw.');
            this.getServiceWorkerInfo(); // Not using it here, just making sure it is fetched and cached.
            return Promise.resolve(true);
        }

        return new Promise((resolve, reject) => {
            let timeout;
            function controllerchangeHandler() {
                console.log('Received controllerchange event.');
                clearTimeout(timeout);
                this.getServiceWorkerInfo(); // Not using it here, just making sure it is fetched and cached.
                navigator.serviceWorker.removeEventListener('controllerchange', controllerchangeHandler);
                resolve(true);
            }

            navigator.serviceWorker.addEventListener('controllerchange', controllerchangeHandler);

            timeout = setTimeout(() => {
                console.error('Timed out waiting for page to become controlled by service worker.');
                navigator.serviceWorker.removeEventListener('controllerchange', controllerchangeHandler);
                reject(false);
            }, 5000);
        });
    },

    async getServiceWorkerInfo() {
        if (serviceWorkerInfo !== undefined) {
            return serviceWorkerInfo;
        }
        serviceWorkerInfo = this
            .postMessageAndAwaitResult('infoRequest')
            .then((info) => {
                console.log('serviceWorkerInfo:', info);
                return info;
            })
            .catch(() => {});

        return serviceWorkerInfo;
    },

    /**
     * @param {string} type
     * @param {object} message
     * @return {Promise<*>}
     */
    async postMessageAndAwaitResult(type, message = {}) {
        return new Promise((resolve) => {
            const responseKey = uuid.v4();
            const listener = (event) => {
                if (event.data?.responseKey === responseKey) {
                    navigator.serviceWorker.removeEventListener('message', listener);
                    resolve(event.data.payload);
                }
            };
            navigator.serviceWorker.addEventListener('message', listener, false);
            navigator.serviceWorker.controller.postMessage({ responseKey, type, payload: message });
        });
    },
};

export default ServiceWorkerService;
